import React from "react";
import { Landmark, Target, Search, Handshake, Users, BarChart3, FileText } from "lucide-react";
import { FeatureHero, FeatureRow, IconGrid, PhotoBand, ClosingCta, FeatureFooterNav, Shot } from "@/components/features/FeatureKit";
import { TeamRolesMock, ProfileFieldsMock, KpiMock } from "@/components/features/mocks";

const PHOTO = "https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1800";

export default function CapitalProfilesFeature() {
  return (
    <div className="overflow-x-hidden" data-testid="feature-capital-profiles-page">
      <FeatureHero
        eyebrow="Capital Profiles"
        icon={Landmark}
        title="Capital belongs in the community too."
        copy="Startup Otter gives investors, funds, angel groups, accelerators, and capital allocators a presence designed specifically for them."
        ctaLabel="Explore Capital Profiles"
        ctaTo="/capital"
        visual={<Shot src="/features/capital-profile.png" alt="The Startup Otter capital profile for Launchpad Accelerator, with cover photo, logo and founder actions" url="startupotter.com/capital/launchpad-accelerator" testId="capital-profile-visual" priority />}
      />

      <FeatureRow
        testId="capital-dedicated"
        label="Purpose-built"
        icon={Target}
        title="Built for capital."
        copy="Capital organizations have their own profile type instead of being squeezed into a generic business or personal page."
        visual={<ProfileFieldsMock title="Investment focus" testId="mock-investment-focus" items={[["Industries", "SaaS · Consumer Apps"], ["Stages", "Pre-Seed · Seed"], ["Type", "Accelerator"], ["Check size", "$50k – $250k"]]} />}
      />

      <FeatureRow
        testId="capital-info"
        tone="dark"
        flip
        label="For founders"
        icon={FileText}
        title="Tell founders who you are."
        copy="Present your organization, focus, background, location, links, and other information founders need to understand your place in the ecosystem."
        visual={<Shot src="/features/capital-profile.png" alt="A capital profile explaining focus, fit and how to get in touch" url="startupotter.com/capital/launchpad-accelerator" dark cropClass="h-[420px]" testId="capital-detail-shot" />}
      />

      <FeatureRow
        testId="capital-discovery"
        tone="tint"
        label="Discovery"
        icon={Search}
        title="Make capital discoverable."
        copy="Become part of Startup Otter's searchable ecosystem and help relevant founders and community members find you."
        visual={<Shot src="/features/capital-directory.png" alt="Filtering the Startup Otter capital directory by stage, check size and focus" url="startupotter.com/capital" testId="capital-directory-shot" />}
      />

      <PhotoBand
        testId="capital-relationships"
        src={PHOTO}
        alt="Investors and founders around a boardroom table"
        kicker="Relationships"
        title="Build relationships before the pitch."
        copy="Follow, fan, participate, and engage with the community instead of only appearing when someone is fundraising."
      />

      <IconGrid
        testId="capital-together"
        tone="tint"
        columns={2}
        title="Run it like a team, measure it like a program."
        items={[
          { icon: Users, title: "Manage the profile together.", copy: "Use Owner, Admin, and Contributor roles to give your capital team appropriate access." },
          { icon: BarChart3, title: "Understand your visibility.", copy: "Qualified users can see how people discover and engage with their Capital presence through Startup Otter Analytics." },
        ]}
      />

      <FeatureRow
        testId="capital-team"
        label="Team roles"
        icon={Handshake}
        title="Everyone gets the right level of access."
        copy="Owners keep control, Admins run the day to day, and Contributors help with the parts they are given."
        visual={<TeamRolesMock testId="mock-capital-team" />}
      />

      <FeatureRow
        testId="capital-analytics"
        tone="tint"
        flip
        label="Analytics"
        icon={BarChart3}
        title="See who is paying attention."
        copy="Visits, unique visitors, meeting requests and pitch-deck interest, all in one dashboard for the presence you manage."
        visual={<KpiMock testId="mock-capital-kpis" items={[{ label: "Capital visits", value: "1,204", delta: "+11%" }, { label: "Unique visitors", value: "486", delta: "+8%" }, { label: "Meetings requested", value: "23", delta: "+5" }, { label: "Pitch decks", value: "37", delta: "+9" }]} />}
      />

      <ClosingCta
        testId="capital-profiles-closing"
        title="More than a directory listing. A place in the ecosystem."
        ctaLabel="Explore Capital Profiles"
        ctaTo="/capital"
      />

      <FeatureFooterNav next={{ to: "/features/events", label: "Events", desc: "Discover where the startup community is gathering—and the people behind it." }} />
    </div>
  );
}
